import { MdClose } from 'react-icons/md';
import { Portal } from '../../components/Modal';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { IEnquiry } from '@wot/typings';
import xhr from '../../utils/fetch';
import { format } from 'date-fns/esm';
import { parseISO } from 'date-fns';
import { DEFAULT_PROFILE_PIC } from '../../assets/icons';
import { Button } from '@wot/components';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import enquiryApi from '../../api/enquiry';

export default function Enquiry() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState<IEnquiry>();
  const { enquiryId } = useParams();

  useEffect(() => {
    if (enquiryId) {
      enquiryApi.getById(enquiryId)
        .then((data) => setEnquiry(data))
    }
  }, [enquiryId]);

  const handleClose = () => {
    navigate(-1);
  };

  if (!enquiry) {
    return null;
  }

  const updateEnquiryStatus = (status: 'active' | 'resolved') => {
    enquiryApi.updateStatus(enquiry._id, { action: status })
      .then(() => {
        toast.success(`Enquiry Resolved for Enquiry Id: ${enquiry._id}`);
        queryClient.invalidateQueries({ queryKey: ['enquiries'] });
        navigate(-1);
      }).catch(err => {
        toast.error("Something went wrong.")
      })
  };

  return (
    <Portal>
      <div className="relative w-full h-screen flex justify-center -z-10">
        <div onClick={() => handleClose()} className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10"></div>
        <div className="w-full h-full mx-auto my-auto flex justify-end">
          <div className="w-[40%] h-full flex flex-col gap-5 bg-white rounded-l-2xl rounded-bl-2xl p-7 overflow-y-scroll">
            <div className="w-full flex flex-col gap-3">
              <div className="w-full flex items-center justify-between">
                <h1 className="text-lg font-semibold">Enquiry {enquiry?._id}</h1>
                <MdClose className="text-2xl cursor-pointer" onClick={() => handleClose()} />
              </div>

              <div className="flex gap-1">
                <p className="text-sm">Status: </p>
                <p className="text-sm font-medium">{enquiry.status}</p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm">Date</p>
                <p className="text-sm font-medium">{format(parseISO(enquiry.createdAt), 'd LLL, yyyy')}</p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm">Issue Raised By</p>
                <p className="text-sm font-medium">{enquiry.name}</p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm">Subject</p>
                <p className="text-sm font-medium">{enquiry.subject}</p>
              </div>
            </div>
            {enquiry.type === 'support' && (
              <>
                {enquiry.studentId && (
                  <div className="w-full flex flex-col gap-2">
                    <h3 className="text-sm font-medium">Student:</h3>
                    <div className="w-full max-w-[180px] flex flex-col gap-4 items-center justify-center p-7 border border-primary rounded-md">
                      <div className="w-10 h-10 rounded-full overflow-hidden">
                        <img src={enquiry.profilePic || DEFAULT_PROFILE_PIC} className="w-full h-full object-cover" />
                      </div>
                      <h2 className="text-base font-medium">{enquiry.name}</h2>
                      <Link to={`/student-management/${enquiry.studentId}`}>
                        <Button type="secondary" text="View Profile" block onClick={() => { }} />
                      </Link>
                    </div>
                  </div>
                )}
                {enquiry.tutorId && (
                  <div className="w-full flex flex-col gap-2">
                    <h3 className="text-sm font-medium">Tutor:</h3>
                    <div className="w-full max-w-[180px] flex flex-col gap-4 items-center justify-center p-7 border border-primary rounded-md">
                      <div className="w-10 h-10 rounded-full overflow-hidden">
                        <img src={enquiry.profilePic || DEFAULT_PROFILE_PIC} className="w-full h-full object-cover" />
                      </div>
                      <h2 className="text-base font-medium">{enquiry.name}</h2>
                      <Link to={`/tutor-management/manage/${enquiry.tutorId}`}>
                        <Button type="secondary" text="View Profile" block onClick={() => { }} />
                      </Link>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="w-full flex flex-col gap-4 p-4 border border-primary rounded-md">
              <p className="text-sm font-medium uppercase text-primary">Comment</p>
              <p className="text-sm">{enquiry.description}</p>
            </div>
            <div className="w-full flex items-center gap-5 mt-auto">
              <Button type="s-outlined" block text="Cancel" onClick={() => navigate(-1)} />
              {enquiry.status === 'active' ? (
                <Button block text="Mark as Resolved" onClick={() => updateEnquiryStatus('resolved')} />
              ) : (
                <Button block text="Mark as Active" onClick={() => updateEnquiryStatus('active')} />
              )}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
